import { tt, useAuth, useFranchiseModules } from '@agos/shared'
import { EmptyState, PageHeader, TabGroup } from '@agos/ui'
import { Languages, Settings2, Store } from 'lucide-react'
import { useMemo, type ComponentType } from 'react'
import { useSearchParams } from 'wouter'

import { SettingsAiTranslatePage } from '../settings-ai-translate/SettingsAiTranslatePage'
import { SettingsEmailPage } from '../settings-email/SettingsEmailPage'
import { SettingsBillingPage } from '../settings-billing/SettingsBillingPage'
import { SettingsGeneralPage } from '../settings-general/SettingsGeneralPage'
import { SettingsPushupPage } from '../settings-pushup/SettingsPushupPage'
import { SettingsMonitoringPage } from '../settings-monitoring/SettingsMonitoringPage'
import { SettingsNotificationsPage } from '../settings-notifications/SettingsNotificationsPage'
import { SettingsSmsPage } from '../settings-sms/SettingsSmsPage'
import { SettingsTranslationsPage } from '../settings-translations/SettingsTranslationsPage'
import { SalesChannelsPage } from '../sales-channels/SalesChannelsPage'
import { SurchargesPage } from '../surcharges/SurchargesPage'
import { VatCategoriesPage } from '../vat-categories/VatCategoriesPage'
import {
  SALES_TABS,
  SETTINGS_TABS,
  TRANSLATION_TABS,
  activeTab,
  stripIsWorthDrawing,
  visibleTabs,
  type HubTab,
} from './settings-hub'

/**
 * `/settings` and `/translations` — FRONTEND.md §9.208.
 *
 * *"Nastavenia — jedna kategória… vyberá sa zvnútra, ako to už funguje pri Vernostnom programe."* That
 * comparison names the shape: *Vernostný program* is one entry on one route with a tab bar inside.
 *
 * **The seven routes these fold up all still answer.** A hub cannot be a wall in front of a page
 * somebody has bookmarked, and every spec that drives `/settings-sms` still drives it.
 *
 * **Which tabs appear is per employee**, because these are not one gate — three of the five flags are
 * held by the waiter in the restored data and `settings_monitoring` is not. The decision lives in
 * `settings-hub.ts` beside the guards it reads, which the test there pins to the routes' own.
 */

const PAGES: Record<string, ComponentType> = {
  '/settings-general': SettingsGeneralPage,
  // §9.290 - a registered route that nothing linked to.
  '/settings-billing': SettingsBillingPage,
  '/settings-sms': SettingsSmsPage,
  '/settings-email': SettingsEmailPage,
  '/settings-pushup': SettingsPushupPage,
  '/settings-notifications': SettingsNotificationsPage,
  '/settings-monitoring': SettingsMonitoringPage,
  '/settings-ai-translate': SettingsAiTranslatePage,
  '/settings-translations': SettingsTranslationsPage,
  // §9.308, owner item A6 - the three configuration screens of *Predaj a účtovanie*.
  '/sales-channels': SalesChannelsPage,
  '/vat-categories': VatCategoriesPage,
  '/surcharges': SurchargesPage,
}

function Hub({
  tabs,
  title,
  description,
  icon,
}: {
  tabs: readonly HubTab[]
  title: string
  description: string
  icon: React.ReactNode
}) {
  const { hasPermission, user } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()
  const [params, setParams] = useSearchParams()

  const mine = useMemo(
    () => visibleTabs(tabs, { systemRole: user?.systemRole, hasPermission, isModuleEnabled }),
    [tabs, user?.systemRole, hasPermission, isModuleEnabled],
  )
  const active = activeTab(mine, params.get('tab'))
  const current = mine.find((tab) => tab.id === active)
  const Page = current === undefined ? undefined : PAGES[current.path]

  return (
    <div className="space-y-5" data-settings-hub={mine.length}>
      <PageHeader title={title} description={description} icon={icon} />

      {/* One tab is not a choice, and a strip of one implies four more that this employee cannot
        * have. `variant="bar"` is what the other eleven top-level tab bars use. */}
      {stripIsWorthDrawing(mine) && (
        <TabGroup
          variant="bar"
          tabs={mine.map((tab) => ({ id: tab.id, label: tab.label }))}
          active={active ?? ''}
          onChange={(id) => {
            // In the URL, because "open Nastavenia → SMS" is a thing somebody sends a colleague, and
            // because a reload that forgets the tab is the complaint this batch is full of.
            const next = new URLSearchParams(params)
            next.set('tab', id)
            setParams(next, { replace: true })
          }}
        />
      )}

      {Page === undefined ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Settings2 className="w-10 h-10" />}
            title={tt('Žiadne nastavenia')}
            description={tt('Na žiadnu z týchto sekcií nemáte oprávnenie.')}
          />
        </div>
      ) : (
        <div data-settings-tab={active}>
          <Page />
        </div>
      )}
    </div>
  )
}

export function SettingsHubPage() {
  return (
    <Hub
      tabs={SETTINGS_TABS}
      title={tt('Nastavenia')}
      description={tt('Všeobecné nastavenia, správy zákazníkom a dohľad nad prevádzkou.')}
      icon={<Settings2 className="w-5 h-5" />}
    />
  )
}

/**
 * `/sales-settings` — the owner's item A6, §9.308.
 *
 * Three sidebar items become one entry with a tab bar, which is the shape §9.208 gave *Nastavenia* and
 * *Preklady* and the owner accepted twice. All three routes still answer.
 */
export function SalesHubPage() {
  return (
    <Hub
      tabs={SALES_TABS}
      title={tt('Predaj a účtovanie')}
      description={tt('Predajné kanály, sadzby DPH a príplatky so zálohami.')}
      icon={<Store className="w-5 h-5" />}
    />
  )
}

export function TranslationsHubPage() {
  return (
    <Hub
      tabs={TRANSLATION_TABS}
      title={tt('Preklady')}
      description={tt('Jazyky a automatický preklad, a editor jednotlivých textov.')}
      icon={<Languages className="w-5 h-5" />}
    />
  )
}
